/**
 * Low-level document.cookie helpers (no portal session imports).
 */

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();

    if (cookie.startsWith(name + '=')) {
      const raw = cookie.substring(name.length + 1);
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
  }

  return null;
}

export function setCookie(
  name: string,
  value: string,
  options: {
    domain?: string;
    path?: string;
    maxAge?: number;
    secure?: boolean;
    sameSite?: 'Strict' | 'Lax' | 'None';
    /** When true, write value without encodeURIComponent (matches portal-v2 Cookie.set). */
    rawValue?: boolean;
  } = {},
): void {
  if (typeof document === 'undefined') {
    return;
  }

  const {
    domain,
    path = '/',
    maxAge,
    secure = true,
    sameSite = 'Lax',
    rawValue = false,
  } = options;

  let cookieString = `${name}=${rawValue ? value : encodeURIComponent(value)}`;

  if (path) {
    cookieString += `; path=${path}`;
  }

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  if (maxAge !== undefined) {
    cookieString += `; max-age=${maxAge}`;
  }

  if (secure) {
    cookieString += '; secure';
  }

  if (sameSite) {
    cookieString += `; samesite=${sameSite}`;
  }

  document.cookie = cookieString;
}

/**
 * Write a parent-domain SSO cookie the same way portal-v2 does:
 * no encodeURIComponent, path=/, optional .falconbrick.com domain, no Secure on http.
 */
export function setPortalCompatibleCookie(
  name: string,
  value: string,
  days = 7,
): void {
  if (typeof document === 'undefined') {
    return;
  }

  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  const expiresPart = `; expires=${expires.toUTCString()}`;
  const host = window.location.host;

  const readRawCookie = (cookieName: string): string | null => {
    const nameEQ = `${cookieName}=`;
    const parts = document.cookie.split(';');
    for (let i = 0; i < parts.length; i++) {
      let c = parts[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length);
      }
    }
    return null;
  };

  if (host.split('.').length === 1) {
    // localhost / single-label host — host-only cookie
    document.cookie = `${name}=${value}${expiresPart}; path=/`;
    return;
  }

  const configuredDomain = (import.meta.env.VITE_COOKIE_DOMAIN as string | undefined)?.trim();
  const primaryDomain = configuredDomain || '.falconbrick.com';
  document.cookie = `${name}=${value}${expiresPart}; path=/; domain=${primaryDomain}`;

  // Fallback if parent domain rejected (same pattern as portal-v2 Cookie.set)
  if (readRawCookie(name) == null || readRawCookie(name) !== value) {
    const fallbackDomain = `.${host}`;
    document.cookie = `${name}=${value}${expiresPart}; path=/; domain=${fallbackDomain}`;
  }
}

export function deleteCookie(
  name: string,
  options: {
    domain?: string;
    path?: string;
  } = {},
): void {
  setCookie(name, '', { ...options, maxAge: 0 });
}
